import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCompanyCategories, toggleCompanyCategoryStatus } from "../../../redux/slice/companyCategorySlice";
import { PlusIcon } from "../../../assets/icons";
import CommonButton from "../../../components/commonButton";
import CompanyCategoryModal from "./CompanyCategoryModal";
import ConfirmModal from "../../../components/withModal/ConfirmModal";
import { usePermission } from "../../../hooks/usePermission";
import { TableSearch } from "../../../components/form/Fields";
import CustomTable from "../../../components/table/CustomTable";
import StatusChip from "../../../components/statusChip/StatusChip";

const MODULE_SLUG = "company_category";

const CompanyCategoryList = () => {
    const dispatch = useDispatch();
    const { list, pagination, isLoading } = useSelector((state) => state.companyCategory);
    const { hasPermission } = usePermission();

    const canCreate = hasPermission(MODULE_SLUG, "create");
    const canUpdate = hasPermission(MODULE_SLUG, "update");

    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState({ value: 10, label: 10 });

    const [showModal, setShowModal] = useState(false);
    const [isView, setIsView] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusItem, setStatusItem] = useState(null);

    // Debounce search
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchData = useCallback(() => {
        dispatch(fetchCompanyCategories({
            page: currentPage,
            limit: limit.value,
            search: debouncedSearch
        }));
    }, [dispatch, currentPage, limit.value, debouncedSearch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleEdit = (item) => {
        setEditItem(item);
        setIsView(false);
        setShowModal(true);
    };

    const handleView = (item) => {
        setEditItem(item);
        setIsView(true);
        setShowModal(true);
    };

    const handleConfirmStatusChange = async () => {
        if (!statusItem) return;
        try {
            const result = await dispatch(
                toggleCompanyCategoryStatus({
                    id: statusItem.id,
                    is_active: !statusItem.is_active,
                })
            ).unwrap();
            if (result.data.success) {
                fetchData();
            }
        } catch (error) {
            // Error handled by slice toast
        } finally {
            setShowStatusModal(false);
            setStatusItem(null);
        }
    };

    const columns = useMemo(() => [
        {
            id: "name",
            header: "Name",
            cell: (_, row) => row.name || "-"
        },
        {
            id: "slug",
            header: "Slug",
            cell: (_, row) => row.slug || "-"
        },
        {
            id: "status",
            header: "Status",
            cell: (_, row) => (
                <StatusChip
                    bg={row.is_active ? "success" : "danger"}
                    pill
                    title={row.is_active ? "Active" : "Inactive"}
                    className={canUpdate ? "cursor-pointer" : ""}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (canUpdate) {
                            setStatusItem(row);
                            setShowStatusModal(true);
                        }
                    }}
                />
            )
        }
    ], [canUpdate]);

    const actionList = useMemo(() => [
        {
            title: "View",
            handler: (row) => handleView(row)
        },
        canUpdate && {
            title: "Edit",
            handler: (row) => handleEdit(row)
        },
        canUpdate && {
            title: (row) => row?.is_active ? "Deactivate" : "Activate",
            handler: (row) => {
                setStatusItem(row);
                setShowStatusModal(true);
            }
        }
    ].filter(Boolean), [canUpdate]);

    const paginationConfig = useMemo(() => ({
        currentPage,
        totalPages: pagination?.totalPages || 1,
        total: pagination?.total || 0,
        onPageChange: (page) => setCurrentPage(page),
        pageSize: limit.value,
        setPageSize: (size) => setLimit({ value: size, label: size }),
        setCurrentPage
    }), [currentPage, pagination, limit]);

    return (
        <div className="bg-white rounded-2 overflow-hidden common-shadow">
            <div className="p-4 d-flex align-items-center justify-content-between border-bottom gap-3">
                <TableSearch value={searchTerm}
                    placeholder="Search company categories..."
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }} />
                {canCreate && (
                    <CommonButton
                        icon={<PlusIcon />}
                        variant="primary"
                        title="Add Category"
                        onClick={() => {
                            setEditItem(null);
                            setIsView(false);
                            setShowModal(true);
                        }}
                    />
                )}
            </div>

            <CustomTable
                data={list || []}
                columns={columns}
                loading={isLoading}
                emptyMessage="No company categories found"
                keyField="id"
                actionList={actionList}
                pagination={paginationConfig}
            />

            <CompanyCategoryModal
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false);
                    setEditItem(null);
                    setIsView(false);
                }}
                title={isView ? "View Company Category" : editItem ? "Edit Company Category" : "Add Company Category"}
                item={editItem}
                isView={isView}
                size="sm"
                refresh={fetchData}
            />

            <ConfirmModal
                isOpen={showStatusModal}
                onClose={() => {
                    setShowStatusModal(false);
                    setStatusItem(null);
                }}
                title={statusItem?.is_active ? "Deactivate Category" : "Activate Category"}
                message={
                    statusItem?.is_active
                        ? "Are you sure you want to deactivate this company category?"
                        : "Are you sure you want to activate this company category?"
                }
                size="sm"
                onConfirm={handleConfirmStatusChange}
            />
        </div>
    );
};

export default CompanyCategoryList;
